import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { QUERY_NAME, ROUTES, SERVICES } from "@lhd/constants";
import { getCompanyId } from "@lhd/utility";
import { notification } from "antd";
import axiosV2 from "@lhd/libs/axiosV2";
import axiosV1 from "@lhd/libs/axios";
import {
  AppRecordV2,
  AppV2,
  CONTRACT_STATUS,
  DuplicateTargetCompanyIdsResponse,
  PermissionV2,
} from "@lhd/types";
import { useNavigate } from "react-router-dom";
import { user } from "@lhd/features/users/selector";
import { useSelector } from "react-redux";
import { isEmpty } from "lodash";
import { APP_TYPES } from "@lhd/components/CloneApp/constants";
import { handleMessageError } from "@lhd/features/Apps/utils";

export const useFetchOneAppByTypeV2 = (
  appType?: string,
  defaultCompanyId?: number | string,
  enable = true,
) => {
  const navigate = useNavigate();
  const companyId = defaultCompanyId || getCompanyId();
  return useQuery(
    ["v2", QUERY_NAME.APP, appType, companyId],
    () => {
      return axiosV2({
        method: "get",
        url: `/${appType}`,
        params: {
          company_id: companyId,
        },
      }).then((res) => {
        return res.data as AppV2;
      });
    },
    {
      cacheTime: 300,
      staleTime: 300,
      retry: 0,
      enabled: !!appType && enable,
      refetchOnWindowFocus: false,
      // refetchOnMount: "always",
      onError({ response }: any) {
        if (response?.status === 403) {
          navigate(`/${ROUTES.FORBIDDEN}`);
        }
        notification["error"]({
          message: response?.data?.message,
        });
      },
    },
  );
};

export const useFetchOneAppV2 = (appType?: string, appId?: string) => {
  const companyId = getCompanyId();
  return useQuery(
    ["v2", QUERY_NAME.APP, appType, appId, companyId],
    () => {
      return axiosV2({
        method: "get",
        url: `/${appType}${appId ? `/${appId}` : ""}`,
        params: {
          company_id: companyId,
        },
      }).then((res) => {
        return res.data as AppV2;
      });
    },
    {
      cacheTime: 300,
      staleTime: 300,
      retry: 0,
      onError({ response }: any) {
        notification["error"]({
          message: response?.data?.error,
        });
      },
      enabled: !!appType,
    },
  );
};

export const useCreateRecordV2 = (params: {
  appType: string;
  appId: string;
  companyId?: number | string;
}) => {
  const { appType, appId } = params;
  const companyId = params.companyId || getCompanyId();
  return useMutation(
    async ({ payload }: any) => {
      let url = `/${appType}/${companyId}${SERVICES.APPS_RECORDS}`;
      if (appType === APP_TYPES.COMPANY_POLICIES) {
        url = `/${appType}/${appId}${SERVICES.APPS_RECORDS}`;
      }
      const data = await axiosV2({
        method: "post",
        url,
        data: payload,
      });
      return data.data as AppRecordV2;
    },
    {
      onSuccess(data: any, variables: any) {
        if (variables.cb) {
          variables.cb();
        }
        notification["success"]({
          message: "保存しました",
        });
      },
      onError(_: any, variables: any) {
        variables.onError && variables.onError();
      },
    },
  );
};

export const useUpdateRecordV2 = (params: {
  appType: string;
  appId: string;
  companyId?: number | string;
}) => {
  const { appType, appId } = params;
  const companyId = params.companyId || getCompanyId();
  const queryClient = useQueryClient();

  return useMutation(
    ({ payload, recordId }: any) => {
      let url = `/${appType}/${companyId}${SERVICES.APPS_RECORDS}/${recordId}`;
      if (appType === APP_TYPES.COMPANY_POLICIES) {
        url = `/${appType}/${appId}${SERVICES.APPS_RECORDS}/${recordId}`;
      }
      return axiosV2({
        method: "put",
        url,
        data: payload,
      });
    },
    {
      onSuccess(data: any, variables: any) {
        if (variables.cb) {
          variables.cb();
        }
        notification["success"]({
          message: "保存しました",
        });
        queryClient.refetchQueries([
          QUERY_NAME.FETCH_RECORD,
          appType,
          appId,
          String(data.data.id),
        ]);
      },
      onError(_: any, variables: any) {
        variables.onError && variables.onError();
      },
    },
  );
};

export const useDeleteRecordsV2 = (params: {
  appType: string;
  appId: string;
  companyId?: number | string;
}) => {
  const { appType, appId } = params;
  const companyId = params.companyId || getCompanyId();

  let url = `/${appType}/${companyId}${SERVICES.APPS_RECORDS}/batch_delete`;
  if (appType === APP_TYPES.COMPANY_POLICIES) {
    url = `/${appType}/${appId}${SERVICES.APPS_RECORDS}/batch_delete`;
  }
  return useMutation(
    ({ payload }: any) =>
      axiosV2({
        method: "post",
        url,
        data: payload,
      }),
    {
      onSuccess(data: any, variables: any) {
        if (variables.cb) {
          variables.cb();
        }
        notification["success"]({
          message: "保存しました",
        });
      },
      onError(error: any, variables: any) {
        variables.onError && variables.onError();
        notification["error"]({
          message: error?.response?.data?.message,
        });
      },
    },
  );
};

export const useDeleteUserTableColumnSettings = (params: {
  tableName: string;
}) => {
  const { tableName } = params;
  const queryClient = useQueryClient();

  return useMutation(
    () =>
      axiosV1({
        method: "delete",
        url: SERVICES.USER_TABLE_COLUMN_SETTINGS,
        params: {
          table_name: tableName,
        },
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([SERVICES.USER_TABLE_COLUMN_SETTINGS]);
      },
      onError(error: any) {
        notification["error"]({
          message: error?.response?.data?.errors?.[0],
        });
      },
    },
  );
};

export const useStartLayoutEdit = (params: { tableName: string }) => {
  const { tableName } = params;
  const queryClient = useQueryClient();

  return useMutation(
    (force?: boolean) =>
      axiosV1({
        method: "post",
        url: `${SERVICES.USER_TABLE_COLUMN_SETTINGS}/layout_edit/start`,
        data: {
          table_name: tableName,
          ...(force ? { force: true } : {}),
        },
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([SERVICES.USER_TABLE_COLUMN_SETTINGS]);
      },
      onError(error: any) {
        notification["error"]({
          message: error?.response?.data?.errors?.[0],
        });
      },
    },
  );
};

export const useResetLayoutEdit = (params: { tableName: string }) => {
  const { tableName } = params;
  const queryClient = useQueryClient();

  return useMutation(
    () =>
      axiosV1({
        method: "post",
        url: `${SERVICES.USER_TABLE_COLUMN_SETTINGS}/layout_edit/reset`,
        data: {
          table_name: tableName,
        },
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([SERVICES.USER_TABLE_COLUMN_SETTINGS]);
      },
      onError(error: any) {
        notification["error"]({
          message: error?.response?.data?.errors?.[0],
        });
      },
    },
  );
};

export const useFinishLayoutEdit = (params: { tableName: string }) => {
  const { tableName } = params;
  const queryClient = useQueryClient();

  return useMutation(
    () =>
      axiosV1({
        method: "post",
        url: `${SERVICES.USER_TABLE_COLUMN_SETTINGS}/layout_edit/finish`,
        data: {
          table_name: tableName,
        },
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([SERVICES.USER_TABLE_COLUMN_SETTINGS]);
      },
      onError(error: any) {
        notification["error"]({
          message: error?.response?.data?.errors?.[0],
        });
      },
    },
  );
};

// /api/v2/:app_type/:app_id/records/list
type UseFetchRecordsV2Params = {
  appType: string;
  appId: string;
  params: {
    page: number;
    pageSize: number;
  };
  filter: any;
  canView?: boolean;
  workerId?: number;
  companyId?: number;
  enabled?: boolean;
};

export const useFetchRecordsV2 = (params: UseFetchRecordsV2Params) => {
  const { appType, appId, filter, canView, workerId, enabled = true } = params;
  const { pageSize, ...restParams } = params.params;
  const companyId = params.companyId || getCompanyId();
  return useQuery(
    [
      QUERY_NAME.FETCH_RECORD,
      appType,
      appId,
      restParams.page,
      pageSize,
      filter,
      canView,
      workerId,
      companyId,
    ],
    () => {
      if (!canView || !appType || !appId || filter.notFound) {
        return {
          data: [] as AppRecordV2[],
          meta: {
            total: 0,
            total_page: 0,
          },
        };
      }
      let url = `/${appType}/${companyId}${SERVICES.APPS_RECORDS}${SERVICES.LIST}`;
      if (appType === APP_TYPES.COMPANY_POLICIES) {
        url = `/${appType}/${appId}${SERVICES.APPS_RECORDS}${SERVICES.LIST}`;
      }
      if (appType === APP_TYPES.LABOR_DOCUMENTS && workerId) {
        url = `${SERVICES.WORKERS}/${workerId}/${appType}${SERVICES.LIST}`;
      }
      return axiosV2({
        method: "post",
        url,
        data: {
          ...restParams,
          per_page: pageSize,
          ...filter,
        },
        params: {
          ...(workerId &&
            appType !== APP_TYPES.LABOR_DOCUMENTS && { worker_id: workerId }),
        },
      }).then((res) => {
        return {
          data: res.data as AppRecordV2[],
          meta: {
            total: Number(res.headers["x-total"]) || 0,
            total_page: Number(res.headers["x-total-pages"]) || 1,
          },
        };
      });
    },
    {
      enabled: !!appId && !!appType && enabled,
      cacheTime: 0,
      staleTime: Infinity,
      retry: false,
      keepPreviousData: true,
      onError({ response }: any) {
        notification["error"]({
          message:
            response?.data?.msg ||
            "レコードが削除されました。もう一度共有側で確認してください。",
        });
      },
    },
  );
};

export const useFetchRecordV2 = (params: {
  appType: string;
  appId: string;
  recordId: string;
  companyId?: number | string;
  isDisabled?: boolean;
  workerId?: string;
  isAdmin?: boolean;
}) => {
  const { appType, appId, recordId, isDisabled } = params;
  const companyId = Number(params.companyId || getCompanyId());
  let url = `/${appType}/${companyId}${SERVICES.APPS_RECORDS}/${recordId}`;
  if (appType === APP_TYPES.COMPANY_POLICIES) {
    url = `/${appType}/${appId}${SERVICES.APPS_RECORDS}/${recordId}`;
  }

  const navigate = useNavigate();
  return useQuery(
    [QUERY_NAME.FETCH_RECORD, appType, appId, String(recordId)],
    () => {
      return axiosV2({
        method: "get",
        url,
      }).then((res) => {
        return {
          data: res.data as AppRecordV2,
          meta: {
            total: res.headers["x-total"] || 0,
            total_page: res.headers["x-total-pages"] || 0,
          },
        };
      });
    },
    {
      enabled: !!appId && !!appType && !isDisabled,
      cacheTime: 0,
      staleTime: Infinity,
      retry: false,
      keepPreviousData: true,
      onError({ response }: any) {
        if (response.status === 403) {
          navigate(`/${ROUTES.FORBIDDEN}`);
        } else {
          notification["error"]({
            message:
              response?.data?.msg ||
              response?.data?.message ||
              "レコードが削除されました。もう一度共有側で確認してください。",
          });
        }
      },
    },
  );
};

//api/v2/apps/:app_id/records/filter_options

export const useFetchFilterOptionsV2 = (params: {
  appId: string;
  data: { app_part_ids?: string[]; field_ids?: string[]; active_filters: any };
  appType: string;
  enabled: boolean;
  companyId?: number | string;
  workerId?: number;
}) => {
  const queryClient = useQueryClient();
  const {
    appId,
    data,
    appType,
    enabled = true,
    companyId = getCompanyId(),
  } = params;
  const queryKey = [
    QUERY_NAME.FETCH_RECORD,
    appType,
    appId,
    "filter_options",
    data,
    companyId,
  ];

  return useQuery(
    queryKey,
    async () => {
      // If any active upstream filter explicitly has 0 selections (empty array),
      // the intersection of rows is empty, so there can be no options available.
      if (data.active_filters) {
        const hasEmptyFilter = Object.values(data.active_filters).some(
          (filterVal) => Array.isArray(filterVal) && filterVal.length === 0,
        );
        if (hasEmptyFilter) {
          const columnId = data.app_part_ids?.[0] || data.field_ids?.[0] || "";
          return { [columnId]: [] };
        }
      }

      let url = `/${appType}/${companyId}${SERVICES.APPS_RECORDS}/filter_options`;
      const payloadData: any = {
        ...data,
        ...(params.workerId && { company_id: companyId }),
      };

      if (params.workerId) {
        url = `workers/${params.workerId}/${appType}/filter_options`;
      }
      if (appType === APP_TYPES.COMPANY_POLICIES) {
        url = `/${appType}/${appId}${SERVICES.APPS_RECORDS}/filter_options`;
      }
      const res = await axiosV2({
        method: "post",
        url,
        data: payloadData,
      });
      const columnId = data.app_part_ids?.[0] || data.field_ids?.[0] || "";

      return {
        [columnId]: res.data?.filter_options[columnId] as string[],
      };
    },
    {
      enabled,
      keepPreviousData: true,
      retry: false,
      onSuccess(data) {
        queryClient.setQueryData(queryKey, data);
      },
    },
  );
};

//other hooks related to AppV2 can be added here
export const usePermissionV2 = (dataApp?: AppV2) => {
  //dummy permission until real permission logic is implemented

  const auth = useSelector(user);
  if (auth?.is_admin) {
    return { view: true, edit: true, delete: true };
  }
  const result: PermissionV2 = {
    view: false,
    edit: false,
    delete: false,
  };

  const permissions = dataApp?.company_permissions;
  if (!permissions) return result;

  // view permission
  if (
    permissions.company_view.includes("all") ||
    permissions.company_view.includes(dataApp.app_type)
  ) {
    result.view = true;
  }

  // edit permission
  if (
    permissions.company_edit.includes("all") ||
    permissions.company_view.includes(dataApp.app_type)
  ) {
    result.edit = true;
  }

  // delete permission
  if (
    permissions.company_delete.includes("all") ||
    permissions.company_view.includes(dataApp.app_type)
  ) {
    result.delete = true;
  }

  return result;
};

//**POST** `/api/v2/:app_type/:app_id/records/batch_save`
export const useSaveRecordsV2 = (appType: string) => {
  const companyId = getCompanyId();
  return useMutation(
    ({ data, cb }: any) =>
      axiosV2({
        method: "post",
        url: `/${appType}/${companyId}${SERVICES.APPS_RECORDS}/batch_save`,
        data,
        params: {
          company_id: companyId,
        },
      }),
    {
      onSuccess(res, variables) {
        variables.cb && variables.cb();
        if (isEmpty(res?.data?.errors)) {
          notification["success"]({
            message: "保存しました",
          });
        } else {
          notification["error"]({
            message: "保存失敗しました",
          });
        }
      },
    },
  );
};

/** POST /api/v2/:app_type/:app_id/records/duplicate */
export type DuplicateRecordsV2Payload = {
  record_ids: number[];
  target_company_ids?: number[];
};

export type DuplicateRecordsV2Variables = {
  payload: DuplicateRecordsV2Payload;
  cb?: () => void;
  onError?: () => void;
};

export const useDuplicateRecordsV2 = (params: {
  appType: string;
  appId: string;
  companyId?: number | string;
}) => {
  const { appType, appId } = params;
  const companyId = params.companyId || getCompanyId();
  let url = `/${appType}/${companyId}${SERVICES.APPS_RECORDS}/duplicate`;
  if (appType === APP_TYPES.COMPANY_POLICIES) {
    url = `/${appType}/${appId}${SERVICES.APPS_RECORDS}/duplicate`;
  }
  return useMutation(
    ({ payload }: DuplicateRecordsV2Variables) =>
      axiosV2({
        method: "post",
        url,
        data: payload,
      }),
    {
      onSuccess(_data: any, variables: DuplicateRecordsV2Variables) {
        variables.cb?.();
        notification["success"]({
          message: "複製しました",
        });
      },
      onError(error: any, variables: DuplicateRecordsV2Variables) {
        variables.onError?.();
        notification["error"]({
          message: handleMessageError(error?.response?.data?.message),
        });
      },
    },
  );
};

export const useGetDuplicateTargetCompanyIds = (
  appType: string,
  appId: string | number,
  enabled: boolean,
  payload: any,
) => {
  return useQuery(
    [QUERY_NAME.FETCH_RECORD, appType, appId, payload, "duplicate_targets"],
    () =>
      axiosV2({
        method: "post",
        url: `/${appType}/${appId}${SERVICES.APPS_RECORDS}/duplicate_targets`,
        data: payload,
      }).then((res) => {
        return res.data as DuplicateTargetCompanyIdsResponse;
      }),
    { enabled },
  );
};
export { useDuplicateRecordsV2 as useDuplicateRecords };

//**POST** `/api/v2/:app_type/:app_id/records/reorder`
export const useReorderRecordsV2 = (params: {
  appType: string;
  appId: string;
  companyId?: number | string;
}) => {
  const { appType, appId } = params;
  const companyId = params.companyId || getCompanyId();
  let url = `/${appType}/${companyId}${SERVICES.APPS_RECORDS}/reorder`;
  if (appType === APP_TYPES.COMPANY_POLICIES) {
    url = `/${appType}/${appId}${SERVICES.APPS_RECORDS}/reorder`;
  }
  return useMutation(
    ({
      payload,
    }: {
      payload: {
        record_ids: number[];
        prev_record_id: number;
        next_record_id: number;
      };
    }) =>
      axiosV2({
        method: "post",
        url,
        data: payload,
      }),
    {
      onSuccess(data: any, variables: any) {
        if (variables.cb) {
          variables.cb();
        }
      },
      onError(error: any, variables: any) {
        if (error?.response?.data?.message?.includes("records not found")) {
          notification["error"]({
            message: "レコードは削除されました。並べ替えに失敗しました。",
          });
        } else {
          notification["error"]({
            message: "並べ替えに失敗しました。",
          });
        }
      },
    },
  );
};

//**POST** `/api/v2/:app_type/:id/records/:record_id/sign`
export const useSignV2 = (params: {
  appType: string;
  appId: string;
  recordId: string;
  companyId?: number;
}) => {
  const companyId = params.companyId || getCompanyId();
  const url = `/${params.appType}/${companyId}${SERVICES.APPS_RECORDS}/${params.recordId}/sign`;

  return useMutation(
    (role: string) =>
      axiosV2({
        method: "post",
        url,
        data: {
          role,
        },
      }),
    {
      onSuccess() {
        notification["success"]({
          message: "署名しました",
        });
      },
      onError(error: any) {
        const message = error.response?.data?.message || "署名に失敗しました。";
        notification["error"]({
          message,
        });
      },
    },
  );
};
//POST /api/v2/employment_contracts/452/records/:company_id/change_status
export const useChangeStatusV2 = (params?: {
  appType: string;
  appId: string;
  recordId: string;
  companyId?: number | string;
}) => {
  const companyId = params?.companyId || getCompanyId();
  const queryClient = useQueryClient();
  return useMutation(
    (payload: any) =>
      axiosV2({
        method: "post",
        url: `/${params?.appType}/${companyId}${SERVICES.APPS_RECORDS}/${params?.recordId}/change_status`,
        data: payload,
      }),
    {
      onSuccess() {
        notification["success"]({
          message: "変更ステータスが成功しました",
        });
        queryClient.invalidateQueries([
          QUERY_NAME.FETCH_RECORD,
          params?.appType,
          params?.appId,
          params?.recordId,
        ]);
      },
      onError(error: any) {
        notification["error"]({
          message: "変更ステータスに失敗しました",
        });
      },
    },
  );
};

/** POST /api/v2/:app_type/:app_id/records/duplicate */
export type CompanyAvailable = {
  id: number;
  name: string;
  contract_status: CONTRACT_STATUS;
};

export const useAvailableDuplicateTargetCompanyIds = (
  appType: string,
  appId: string | number,
  enabled?: boolean,
) => {
  return useQuery(
    [QUERY_NAME.FETCH_RECORD, appType, appId, "available_duplicate_targets "],
    () =>
      axiosV2({
        method: "get",
        url: `/${appType}/${appId}${SERVICES.APPS_RECORDS}/available_duplicate_targets `,
      }).then((res) => {
        return res.data as { companies: CompanyAvailable[] };
      }),
    {
      enabled,
    },
  );
};

/** POST /api/v2/workers/for_page_workers/app_record_filter_options */
export const useFetchWorkerAppRecordFilterOptionsV2 = (params: {
  data: Record<string, any>;
  appType?: string;
  enabled: boolean;
  companyId?: number | string;
}) => {
  const queryClient = useQueryClient();
  const { data, appType, enabled = true, companyId = getCompanyId() } = params;
  const queryKey = [
    QUERY_NAME.WORKERS,
    "app_record_filter_options",
    appType,
    companyId,
    data,
  ];

  return useQuery(
    queryKey,
    async () => {
      const res = await axiosV2({
        method: "post",
        url: `${SERVICES.WORKERS}/for_page_workers/app_record_filter_options`,
        data: {
          ...data,
          company_id: companyId,
          app_type: appType,
        },
      });

      return res.data;
    },
    {
      enabled,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      retry: false,
      onSuccess(result) {
        queryClient.setQueryData(queryKey, result);
      },
    },
  );
};

export const useOrderRecordV2 = (params: {
  appType: string;
  appId: string;
  companyId?: number | string;
}) => {
  const { appType, appId } = params;
  const companyId = params.companyId || getCompanyId();
  return useMutation(
    async ({ payload }: any) => {
      let url = `/${appType}/${companyId}${SERVICES.APPS_RECORDS}/reorder`;
      if (appType === APP_TYPES.COMPANY_POLICIES) {
        url = `/${appType}/${appId}${SERVICES.APPS_RECORDS}`;
      }
      const data = await axiosV2({
        method: "post",
        url,
        data: payload,
      });
      return data.data as AppRecordV2;
    },
    {
      onSuccess(data: any, variables: any) {
        if (variables.cb) {
          variables.cb();
        }
        notification["success"]({
          message: "保存しました",
        });
      },
      onError(_: any, variables: any) {
        variables.onError && variables.onError();
      },
    },
  );
};
